import { type To } from 'react-router';

import { type SuggestedChangeChangeTypeEnum } from 'src/graphql/schema';
import { ESCROW_INVITATION_SEARCH_PARAM } from 'src/shared/constants';

import { type ITaskType } from './types';

const notificationTypeMap: Record<ITaskType, string> = {
  SoftwareEscrow: '/software-escrows',
  SaasEscrow: '/saas-escrows',
  ContinuityEscrow: '/continuity-escrows',
  CustodianProfile: '/custodian-profiles',
  Company: '/settings',
  App: '/applications',
};

const changeTypeSlugMap: Record<SuggestedChangeChangeTypeEnum, string> = {
  DepositSuggestedChange: 'deposits',
  EnvironmentSuggestedChange: 'environment',
  AgreementSuggestedChange: 'settings/agreement',
  BeneficiarySuggestedChange: 'settings/beneficiary',
  DepositorSuggestedChange: 'settings/depositor',
  NotificationSuggestedChange: 'settings/technical',
};

export type ITaskUrlResult = { to: To; state?: Record<string, unknown> };

type IStrategyArgs = {
  baseUrl: string;
  parentId: string | undefined;
  recordId: string;
  resolvedParentType: ITaskType;
  payload: { change_type?: SuggestedChangeChangeTypeEnum };
};

type IStrategy = (args: IStrategyArgs) => ITaskUrlResult;

const slug =
  (s: string): IStrategy =>
  ({ baseUrl, parentId }) => ({ to: [baseUrl, parentId, s].join('/') });

const strategies: Partial<Record<string, IStrategy>> = {
  escrow_invitation_sent: ({ recordId: invitationId }) => ({
    to: { search: `?${new URLSearchParams({ [ESCROW_INVITATION_SEARCH_PARAM]: invitationId })}` },
  }),
  escrow_invitation_rejected: slug('overview'),
  escrow_signed: ({ baseUrl, parentId, resolvedParentType }) => ({
    to: [baseUrl, parentId, resolvedParentType === 'ContinuityEscrow' ? 'environment' : 'deposits'].join('/'),
  }),
  suggested_change_created: ({ baseUrl, parentId, payload }) => ({
    to: [baseUrl, parentId, payload.change_type ? changeTypeSlugMap[payload.change_type] : undefined]
      .filter(Boolean)
      .join('/'),
  }),
  agreement_sent_for_review_beneficiary: slug('agreement'),
  agreement_sent_for_review_depositor: slug('agreement'),
  agreement_resent_for_review_depositor: slug('agreement'),
  agreement_resent_for_review_beneficiary: slug('agreement'),
  signatures_requested: slug('agreement'),
  asset_disconnected: slug('deposits'),
  escrow_pending_activation: slug('environment'),
  continuity_provider_removed: slug('environment'),
  continuity_provider_access_issues: slug('environment'),
  payment_source_expiring: ({ baseUrl }) => ({ to: `${baseUrl}/billing` }),
  payment_source_expired: ({ baseUrl }) => ({ to: `${baseUrl}/billing` }),
  custodian_profile_terminated: slug('overview'),
  app_request_created: () => ({ to: '/applications/vendor-requests' }),
};

export function setTaskUrl({
  parentId,
  recordId,
  parentType,
  eventType,
  payload,
}: {
  parentId?: string;
  recordId: string;
  parentType: string;
  eventType: string;
  payload: { escrow_type?: string; change_type?: SuggestedChangeChangeTypeEnum };
}): ITaskUrlResult {
  const resolvedParentType = (parentType === 'Escrow' ? payload.escrow_type : parentType) as ITaskType;
  const baseUrl = notificationTypeMap[resolvedParentType];
  const strategy = strategies[eventType];

  if (strategy) {
    return strategy({ baseUrl, parentId, resolvedParentType, payload, recordId });
  }

  return { to: [baseUrl, parentId].filter(Boolean).join('/') };
}
